import { useRef, useState } from "react";
import { toast } from "sonner";

import type { KbUpload } from "@/shared/types";

/* Files and folders dragged from the desktop onto a place (a table row, a rail item, the
   open folder). `over` lights the target while they hover it; the innermost target takes
   the drop. A dropped folder is walked with the browser's directory reader, so its files
   keep their paths and the server makes the folders they name. */

const entryFile = (entry: FileSystemFileEntry) => new Promise<File>((resolve, reject) => entry.file(resolve, reject));

async function readFolder(folder: FileSystemDirectoryEntry, prefix: string): Promise<KbUpload[]> {
  const reader = folder.createReader();
  const entries: FileSystemEntry[] = [];
  // readEntries hands over at most 100 entries a call; an empty answer means the end
  for (;;) {
    const more = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!more.length) break;
    entries.push(...more);
  }
  const path = `${prefix}${folder.name}/`;
  // dot files (Finder's .DS_Store) are the system's, never the person's knowledge
  const visible = entries.filter((e) => !e.name.startsWith("."));
  const parts = await Promise.all(
    visible.map(async (e) =>
      e.isDirectory ? readFolder(e as FileSystemDirectoryEntry, path) : [{ file: await entryFile(e as FileSystemFileEntry), path: `${path}${e.name}` }],
    ),
  );
  // TODO: an empty dropped folder sends no file, so nothing is made for it
  return parts.flat();
}

export function useFileDrop(onDrop?: (uploads: KbUpload[]) => void) {
  const [over, setOver] = useState(false);
  const depth = useRef(0); // dragenter/leave fire for every child the pointer crosses
  if (!onDrop) return { over: false, props: {} };

  const carriesFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const props = {
    onDragEnter: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current += 1;
      setOver(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return;
      e.stopPropagation();
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      depth.current = 0;
      setOver(false);
      // taken during the event, as the list empties once it returns; each item with its own
      // file, since a drag can also carry text items that would shift a pairing by position
      const items = Array.from(e.dataTransfer.items ?? []).filter((item) => item.kind === "file");
      const dropped = items.length
        ? items.map((item) => ({ entry: item.webkitGetAsEntry?.() ?? null, file: item.getAsFile() }))
        : Array.from(e.dataTransfer.files).map((file) => ({ entry: null, file }));
      Promise.all(
        dropped.map(({ entry, file }) =>
          entry?.isDirectory ? readFolder(entry as FileSystemDirectoryEntry, "") : file ? [{ file, path: file.name }] : [],
        ),
      ).then((parts) => onDrop(parts.flat()), () => toast.error("Couldn't read the dropped folder. Try Upload folder instead."));
    },
  };
  return { over, props };
}
